import React, { useState } from 'react';
import { Button, Display } from 'Views/Components/Button/Button';
import { confirmModal } from 'Views/Components/Modal/ModalUtils';
import alertToast from 'Util/ToastifyUtils';
import { activateUserRequest, deactivateUserRequest } from 'Queries/REST/Accounts/AccountRequests';
import { UserStatusFields } from 'Util/UserUtils';
import { StaffUserEntityAttributes } from 'Views/Pages/Frontend/UserManagement/StaffDetailPage';
import { hasAnyRole } from 'Util/SecurityUtils';
import useStore from 'Hooks/useStore';
import { IUserEntityAttributes } from 'Models/Entities';

interface ActivateDeactivateButtonProps {
	user: (StaffUserEntityAttributes & UserStatusFields) | (IUserEntityAttributes & UserStatusFields);
	onAfterSave: (user: StaffUserEntityAttributes & UserStatusFields) => void;
}

const ActivateDeactivateButton = (props: ActivateDeactivateButtonProps) => {
	const { user, onAfterSave } = props;
	const store = useStore();
	const [pendingRequest, setPendingRequest] = useState(false);

	const isDeactivated = user.lockoutEnd !== null && new Date(user.lockoutEnd) > new Date();

	const deactivateUser = async () => {
		await confirmModal(
			'Deactivate user?',
			'This action will restrict their access to the platform,'
			+ ' and they will no longer be able to log in or access any resources.'
			+ ' This action can be reversed by reactivating the user.',
			{
				confirmText: 'Deactivate User',
				cancelText: 'Cancel',
				resolveOnCancel: true,
				className: 'dangerous-action-modal',
			},
		).then(result => {
			if (!result) {
				return;
			}

			if (!user.email) {
				alertToast('Invalid user', 'error');
				return;
			}
			setPendingRequest(true);
			return deactivateUserRequest(user.email)
				.then(res => {
					alertToast('User deactivated', 'success');
					onAfterSave({
						...user,
						lockoutEnd: new Date(res.data!),
					});
				})
				.catch(err => {
					alertToast(err.response.data, 'error');
				})
				.finally(() => {
					setPendingRequest(false);
				});
		});
	};

	const activateUser = async () => {
		await confirmModal(
			'Reactivate user?',
			'This action will restore their access to the platform,'
			+ ' allowing them to log in and access resources as usual',
			{
				confirmText: 'Reactivate User',
				cancelText: 'Cancel',
				resolveOnCancel: true,
			},
		).then(result => {
			if (!result) {
				return;
			}

			if (!user.email) {
				alertToast('Invalid user', 'error');
				return;
			}

			setPendingRequest(true);
			return activateUserRequest(user.email)
				.then(() => {
					alertToast('User activated', 'success');

					onAfterSave({
						...user,
						lockoutEnd: null,
					});
				})
				.catch(err => {
					alertToast(err.response.data, 'error');
				})
				.finally(() => {
					setPendingRequest(false);
				});
		});
	};

	if (!hasAnyRole(store, ['Admin', 'InstitutionAdmin'])) {
		return null;
	}

	return (
		<Button
			className={!isDeactivated ? 'deactivate' : ''}
			onClick={() => isDeactivated ? activateUser() : deactivateUser()}
			display={Display.Outline}
			disabled={pendingRequest}
			testId="deactivate-btn"
		>
			{isDeactivated ? 'Reactivate' : 'Deactivate'}
		</Button>
	);
};

export default ActivateDeactivateButton;
